/**
 * Daily Reveal prop market registry — DraftKings Sportsbook Partners mappings.
 *
 * Prefer stable `market.marketType.id` values verified via live capture
 * (docs/daily-reveal-prop-market-mapping.md). Regex aliases are fallback only.
 */

import type { DailyRevealPropOperator } from './daily-reveal';
import type { SportCode } from './leagues';

export type PropMarketImplementationStatus =
  | 'verified_settlement_ready'
  | 'verified_settlement_missing'
  | 'inferred'
  | 'needs_live_response'
  | 'unsupported';

export type PropMarketOutcomeType =
  | 'over_under'
  | 'yes_no'
  | 'milestone'
  | 'one_sided'
  | 'scorer_list';

export type PropMarketSelectionMode =
  | 'over'
  | 'milestone_n_plus'
  | 'yes'
  | 'scorer_list'
  | 'x_or_fewer';

export type PropMarketRole = 'batter' | 'pitcher' | 'offense' | 'defense' | 'scorer' | 'any';

export type DailyRevealPropMarketDefinition = {
  sport: SportCode | 'SOCCER';
  key: string;
  label: string;
  /** Verified Partners `market.marketType.id` values. */
  providerMarketTypeIds: string[];
  /** Fallback display-name fragments when type id is absent. */
  providerAliases: string[];
  /** Exact marketType.name values (case-insensitive match). */
  providerMarketTypeNames: string[];
  contextGuards: {
    requirePlayerPropsTag?: boolean;
    excludeNamePatterns?: RegExp[];
    role?: PropMarketRole;
    selectionModes: PropMarketSelectionMode[];
  };
  outcomeType: PropMarketOutcomeType;
  lineRequired: boolean;
  defaultOperator: DailyRevealPropOperator;
  settlementStatTypes: string[];
  implementationStatus: PropMarketImplementationStatus;
};

const TEAM_OR_GAME_EXCLUSIONS = [
  /\bteam total\b/i,
  /\btotal hits\b/i,
  /\btotal runs\b/i,
  /\bgame total\b/i,
  /\bboth teams\b/i,
  /\brace to\b/i,
];

export const DAILY_REVEAL_PROP_MARKET_REGISTRY: DailyRevealPropMarketDefinition[] = [
  // ── MLB settlement-ready ─────────────────────────────────────────────
  {
    sport: 'MLB',
    key: 'hitting_h',
    label: 'Hits',
    providerMarketTypeIds: ['14035', '13227'],
    providerMarketTypeNames: ['Hits O/U', 'Hits Milestones'],
    providerAliases: ['hits o/u', ' hits'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'batter',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        /\bhits allowed\b/i,
        /\bextra base hits\b/i,
        /\bhits \+ runs\b/i,
        /\bhits \+ stolen\b/i,
        /\bhits \+ walks\b/i,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['hitting_h'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'MLB',
    key: 'hitting_runs',
    label: 'Runs',
    providerMarketTypeIds: ['14027', '14463'],
    providerMarketTypeNames: ['Runs O/U', 'Runs (Batter) Milestones'],
    providerAliases: ['runs o/u', 'runs (batter)'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'batter',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [
        ...TEAM_OR_GAME_EXCLUSIONS,
        /\bruns \+ rbis?\b/i,
        /\bhits \+ runs\b/i,
        /\bearned runs\b/i,
        /\bruns allowed\b/i,
      ],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['hitting_runs'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'MLB',
    key: 'hitting_rbi',
    label: 'RBIs',
    providerMarketTypeIds: ['14023', '13230'],
    providerMarketTypeNames: ['RBIs O/U', 'RBIs Milestones'],
    providerAliases: ['rbis o/u', 'rbis'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'batter',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [/\bhits \+ runs \+ rbis?\b/i, /\bruns \+ rbis?\b/i],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['hitting_rbi'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'MLB',
    key: 'hitting_hr',
    label: 'Home Runs',
    providerMarketTypeIds: ['13233'],
    providerMarketTypeNames: ['Home Runs Milestones'],
    providerAliases: ['home runs'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'batter',
      selectionModes: ['milestone_n_plus'],
      excludeNamePatterns: [/1st 5 inning/i, /\brace to\b/i],
    },
    outcomeType: 'milestone',
    lineRequired: false,
    defaultOperator: 'over_or_true',
    settlementStatTypes: ['hitting_hr'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'MLB',
    key: 'hitting_bb',
    label: 'Walks',
    providerMarketTypeIds: ['14039', '14469'],
    providerMarketTypeNames: ['Walks (Batter) O/U', 'Walks (Batter) Milestones'],
    providerAliases: ['walks (batter)'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'batter',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [/\bwalks allowed\b/i, /\bhits \+ walks\b/i],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['hitting_bb'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'MLB',
    key: 'hitting_sb',
    label: 'Stolen Bases',
    providerMarketTypeIds: ['14024', '14461'],
    providerMarketTypeNames: ['Stolen Bases O/U', 'Stolen Bases Milestones'],
    providerAliases: ['stolen bases'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'batter',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [/\bhits \+.*stolen\b/i, /\bruns \+.*stolen\b/i],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_true',
    settlementStatTypes: ['hitting_sb'],
    implementationStatus: 'verified_settlement_ready',
  },
  {
    sport: 'MLB',
    key: 'pitching_k',
    label: 'Pitcher Strikeouts',
    providerMarketTypeIds: ['14018', '13228'],
    providerMarketTypeNames: ['Strikeouts Thrown O/U', 'Strikeouts Thrown Milestones'],
    providerAliases: ['strikeouts thrown'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'pitcher',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [/\bstrikeouts \(batter\)\b/i],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over',
    settlementStatTypes: ['pitching_k'],
    implementationStatus: 'verified_settlement_ready',
  },

  // ── NFL settlement-ready ─────────────────────────────────────────────
  {
    sport: 'NFL',
    key: 'anytime_touchdown',
    label: 'Anytime Touchdown',
    providerMarketTypeIds: ['11019'],
    providerMarketTypeNames: ['Anytime Touchdown Scorer'],
    providerAliases: ['anytime td', 'anytime touchdown', 'to score a touchdown'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'scorer',
      selectionModes: ['scorer_list'],
      excludeNamePatterns: [/passing td/i, /pass td/i, /rushing td/i, /receiving td/i],
    },
    outcomeType: 'scorer_list',
    lineRequired: false,
    defaultOperator: 'over_or_true',
    settlementStatTypes: ['total_touchdowns'],
    implementationStatus: 'verified_settlement_ready',
  },

  // ── NFL inferred (name aliases until live type ids captured) ──────────
  {
    sport: 'NFL',
    key: 'rushing_yards',
    label: 'Rushing Yards',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['rushing yards', 'rush yards', 'rush yds'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'offense',
      selectionModes: ['over'],
      excludeNamePatterns: [/all.?purpose/i, /rush \+ rec/i],
    },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over',
    settlementStatTypes: ['rushing_yards', 'rush_yards'],
    implementationStatus: 'inferred',
  },
  {
    sport: 'NFL',
    key: 'passing_yards',
    label: 'Passing Yards',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['passing yards', 'pass yards', 'pass yds'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'offense',
      selectionModes: ['over'],
    },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over',
    settlementStatTypes: ['passing_yards', 'pass_yards'],
    implementationStatus: 'inferred',
  },
  {
    sport: 'NFL',
    key: 'receiving_yards',
    label: 'Receiving Yards',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['receiving yards', 'rec yards', 'rec yds', 'reception yards', 'recv yards'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'offense',
      selectionModes: ['over'],
      excludeNamePatterns: [/all.?purpose/i, /rush \+ rec/i],
    },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over',
    settlementStatTypes: ['receiving_yards', 'rec_yards'],
    implementationStatus: 'inferred',
  },
  {
    sport: 'NFL',
    key: 'passing_touchdowns',
    label: 'Passing Touchdowns',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['passing td', 'pass td', 'passing touchdowns'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'offense',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [/anytime/i, /to score/i, /scorer/i],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['passing_touchdowns', 'pass_touchdowns'],
    implementationStatus: 'inferred',
  },
  {
    sport: 'NFL',
    key: 'sacks',
    label: 'Sacks',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['sacks', 'sack'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'defense',
      selectionModes: ['over', 'milestone_n_plus'],
      excludeNamePatterns: [/team total/i],
    },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['sacks', 'defense_sacks'],
    implementationStatus: 'inferred',
  },
  {
    sport: 'NFL',
    key: 'all_purpose_yards',
    label: 'All-Purpose Yards',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['all-purpose yards', 'all purpose yards', 'rush + rec yards', 'rushing + receiving'],
    contextGuards: {
      requirePlayerPropsTag: true,
      role: 'offense',
      selectionModes: ['over'],
    },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over',
    settlementStatTypes: ['rushing_yards', 'rush_yards', 'receiving_yards', 'rec_yards'],
    implementationStatus: 'inferred',
  },

  // ── NBA / NCAAMB inferred (until in-season capture) ──────────────────
  {
    sport: 'NBA',
    key: 'points',
    label: 'Points',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['points'],
    contextGuards: { requirePlayerPropsTag: true, role: 'any', selectionModes: ['over'] },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over',
    settlementStatTypes: ['points'],
    implementationStatus: 'inferred',
  },
  {
    sport: 'NBA',
    key: 'three_points_made',
    label: 'Three-Pointers Made',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['three pointers made', '3-pointers made', 'threes made'],
    contextGuards: { requirePlayerPropsTag: true, role: 'any', selectionModes: ['over'] },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over',
    settlementStatTypes: ['three_points_made'],
    implementationStatus: 'inferred',
  },
  {
    sport: 'NBA',
    key: 'rebounds',
    label: 'Rebounds',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['rebounds'],
    contextGuards: { requirePlayerPropsTag: true, role: 'any', selectionModes: ['over'] },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over',
    settlementStatTypes: ['rebounds'],
    implementationStatus: 'inferred',
  },
  {
    sport: 'NBA',
    key: 'assists',
    label: 'Assists',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['assists'],
    contextGuards: { requirePlayerPropsTag: true, role: 'any', selectionModes: ['over'] },
    outcomeType: 'over_under',
    lineRequired: true,
    defaultOperator: 'over',
    settlementStatTypes: ['assists'],
    implementationStatus: 'inferred',
  },
  {
    sport: 'NBA',
    key: 'steals',
    label: 'Steals',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['steals'],
    contextGuards: { requirePlayerPropsTag: true, role: 'any', selectionModes: ['over', 'milestone_n_plus'] },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['steals'],
    implementationStatus: 'inferred',
  },
  {
    sport: 'NBA',
    key: 'blocks',
    label: 'Blocks',
    providerMarketTypeIds: [],
    providerMarketTypeNames: [],
    providerAliases: ['blocks'],
    contextGuards: { requirePlayerPropsTag: true, role: 'any', selectionModes: ['over', 'milestone_n_plus'] },
    outcomeType: 'over_under',
    lineRequired: false,
    defaultOperator: 'over_or_equal',
    settlementStatTypes: ['blocks'],
    implementationStatus: 'inferred',
  },
];

// NCAAMB reuses NBA keys with same definitions
for (const def of [...DAILY_REVEAL_PROP_MARKET_REGISTRY]) {
  if (def.sport === 'NBA') {
    DAILY_REVEAL_PROP_MARKET_REGISTRY.push({ ...def, sport: 'NCAAMB' });
  }
}

export function getPropMarketDefinitionsForSport(
  sport: string,
  options?: { settlementReadyOnly?: boolean }
): DailyRevealPropMarketDefinition[] {
  const code = sport.trim().toUpperCase();
  return DAILY_REVEAL_PROP_MARKET_REGISTRY.filter(d => {
    if (d.sport !== code) return false;
    if (options?.settlementReadyOnly) {
      return d.implementationStatus === 'verified_settlement_ready';
    }
    return d.implementationStatus !== 'unsupported';
  });
}

export function getPropMarketDefinition(
  sport: string,
  key: string
): DailyRevealPropMarketDefinition | undefined {
  const code = sport.trim().toUpperCase();
  return DAILY_REVEAL_PROP_MARKET_REGISTRY.find(d => d.sport === code && d.key === key);
}

export function resolveAllowedPropMarketKeys(
  sport: string,
  allowedMarketKeys: string[] | null | undefined,
  fallbackPropStatType?: string | null
): string[] {
  if (allowedMarketKeys && allowedMarketKeys.length > 0) {
    return [...new Set(allowedMarketKeys.filter(Boolean))];
  }
  if (fallbackPropStatType) return [fallbackPropStatType];
  return getPropMarketDefinitionsForSport(sport, { settlementReadyOnly: true }).map(d => d.key);
}

export type MarketMatchInput = {
  marketTypeId?: string | null;
  marketTypeName?: string | null;
  marketName?: string | null;
  category?: string | null;
  tags?: string[] | null;
  hasParticipant?: boolean;
};

export function marketMatchesDefinition(
  market: MarketMatchInput,
  def: DailyRevealPropMarketDefinition
): boolean {
  const label = [market.marketName, market.marketTypeName, market.category, ...(market.tags ?? [])]
    .filter(Boolean)
    .join(' ');

  for (const exclude of def.contextGuards.excludeNamePatterns ?? []) {
    if (exclude.test(label) || (market.marketName && exclude.test(market.marketName))) {
      return false;
    }
  }

  const typeId = market.marketTypeId ? String(market.marketTypeId) : null;
  const typeName = (market.marketTypeName ?? '').trim().toLowerCase();
  const matchedById = typeId != null && def.providerMarketTypeIds.includes(typeId);
  const matchedByTypeName =
    typeName.length > 0 &&
    def.providerMarketTypeNames.some(n => n.trim().toLowerCase() === typeName);
  const matchedByAlias =
    !matchedById &&
    !matchedByTypeName &&
    def.providerAliases.some(alias => new RegExp(alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i').test(label));

  if (!matchedById && !matchedByTypeName && !matchedByAlias) return false;

  if (def.contextGuards.requirePlayerPropsTag) {
    const tags = market.tags ?? [];
    const hasPlayerProps = tags.some(t => /playerprops/i.test(t));
    const hasParticipant = Boolean(market.hasParticipant);
    // Allow participant or extractable player-prop name when tags omitted in tests/feeds
    if (!hasPlayerProps && !hasParticipant && !looksLikeNamedPlayerProp(market.marketName ?? '')) {
      return false;
    }
  }

  return true;
}

function looksLikeNamedPlayerProp(name: string): boolean {
  if (!name) return false;
  // "{First} {Last} Hits O/U" style
  return /\b[A-Z][a-z]+(?:\s+[A-Z][a-z'.-]+)+\b/.test(name) && !/^(Total|Team|Moneyline|Spread)\b/i.test(name);
}

export function findMatchingPropDefinitions(
  sport: string,
  market: MarketMatchInput,
  allowedKeys?: string[] | null
): DailyRevealPropMarketDefinition[] {
  const defs = getPropMarketDefinitionsForSport(sport).filter(d => {
    if (!allowedKeys || allowedKeys.length === 0) return true;
    return allowedKeys.includes(d.key);
  });
  return defs.filter(d => marketMatchesDefinition(market, d));
}
